import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../../redux/oldStore';
import { IState, ReduxMap } from '../../../redux/redux';
import { ServerResponse } from '../../../utils/Server';
import { IJudge } from '../../JudgingDashboard/Dashboard';
import { ICategoryCaptainInfoSummary, IJudgeInfoSummary, IListItem } from './CategoryCaptainTypes';
import Button from '../../_Core/Button';
import TagComponent from '../../Tags/TagComponent';
import { Tag } from '../../../utils/CommonTypes/PeoplePageTypes';
import Icon, { IconType } from '../../Icon/Icon';
import Select from 'react-select';
import { ActionMeta, InputActionMeta } from 'react-select';
import { Selectable } from '../../../utils/CommonTypes/CommonTypes';

interface IAssignJudgesToCategoryWizardProps extends IState {}

interface icategoryStat {
  id: number;
  numberOfProjects: number;
  numberOfJudges: number;
  name: string;
}

const AssignJudgesToCategoryWizard = (props: IAssignJudgesToCategoryWizardProps) => {
  const [info, setInfo] = useState<ICategoryCaptainInfoSummary>();
  const [tagDic, setTagDic] = useState<Record<number, Tag>>({});
  const [categoryStats, setCategoryStats] = useState<Record<number, icategoryStat>>({});
  const [oldCategoryDic, setOldCategoryDic] = useState<Record<number, number>>({});
  const [stats, setStats] = useState<icategoryStat[]>([]);
  const [showParticipating, setShowParticipating] = useState<boolean>(false);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [showAssigned, setShowAssigned] = useState<boolean>(true);
  const [filters, setFilters] = useState<Selectable[]>([]);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    let stats: Record<number, icategoryStat> = {};

    if (info && info.Judges) {
      let list: icategoryStat[] = [];
      info.ProjectsByCategoryStats.forEach((x) => {
        stats[x.CategoryId] = { id: +x.CategoryId, numberOfJudges: 0, numberOfProjects: x.Number, name: x.CategoryName };
        list.push(stats[x.CategoryId]);
      });

      info.Judges.forEach((x) => {
        if (x.AssignedCategory && stats[x.AssignedCategory]) {
          stats[x.AssignedCategory].numberOfJudges++;
        }
      });

      setCategoryStats(stats);
      setStats(list);
    }
  }, [info, info?.Judges]);

  const init = () => {
    store.server.getApi<ServerResponse<ICategoryCaptainInfoSummary>>('../JudgingCaptain/InfoSummary').then((x) => {
      if (x.Success) {
        let dic = {};
        x.Value.Tags?.forEach((t) => {
          dic[t.Id] = t;
        });
        setTagDic(dic);
        dic = {};
        x.Value.JudgesOldAssignedCategory?.forEach((j) => {
          dic[j.PersonId] = j.AssignedCategory;
        });
        setOldCategoryDic(dic);
        console.log(dic);
        setInfo(x.Value);
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const updateCategory = (judge: IJudgeInfoSummary, category: number) => {
    store.server.postApi<ServerResponse<boolean>>('../JudgingCaptain/AssignCategory', { judgeId: judge.PersonId, categoryId: category }).then((x) => {
      if (x.Success) {
        let judges = [...(info?.Judges ?? [])];
        for (let i = 0; i < judges.length; i++) {
          let j = judges[i];
          if (j.PersonId === judge.PersonId) {
            j.AssignedCategory = category;
            break;
          }
        }
        if (info) setInfo({ ...info, Judges: judges });
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const clearAllAssignments = () => {
    if (!window.confirm('Are you sure you want to clear ALL judge category assignments? This cannot be undone.')) return;
    store.server.postApi<ServerResponse<boolean>>('../JudgingCaptain/ClearAllAssignments', {}).then((x) => {
      if (x.Success) {
        toast.success('All assignments cleared.');
        init();
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  // Filter and sort judges based on category preferences
  const getFilteredAndSortedJudges = (judges: IJudgeInfoSummary[], filters: Selectable[]): IJudgeInfoSummary[] => {
    if (!judges) return [];

    const judgesFilteredByParticipating = showParticipating
      ? judges.filter((judge) => (judge.PlanToParticipate || '').trim().toLowerCase() === 'yes')
      : judges;

    // If no filters are selected, return all judges sorted by LastName, FirstName
    if (!filters || filters.length === 0) {
      return [...judgesFilteredByParticipating].sort((a, b) => {
        const lastNameCompare = (a.LastName || '').localeCompare(b.LastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.FirstName || '').localeCompare(b.FirstName || '');
      });
    }

    // Extract filter values (category IDs)
    const filterValues = filters.map((f) => Number(f.value));

    // Filter judges who have ANY of the selected categories in Category1, Category2, or Category3
    const filteredJudges = judgesFilteredByParticipating.filter((judge) => {
      return filterValues.some((categoryId) => judge.Category1 === categoryId || judge.Category2 === categoryId || judge.Category3 === categoryId);
    });

    // Sort filtered judges with priority order
    return filteredJudges.sort((a, b) => {
      // Helper function to check if judge has any filter category in specific preference slot
      const hasCategory1Match = (judge: IJudgeInfoSummary) => filterValues.includes(judge.Category1);
      const hasCategory2Match = (judge: IJudgeInfoSummary) => filterValues.includes(judge.Category2);
      const hasCategory3Match = (judge: IJudgeInfoSummary) => filterValues.includes(judge.Category3);

      // Priority 1: Category1 matches first
      const aCategory1Match = hasCategory1Match(a);
      const bCategory1Match = hasCategory1Match(b);
      if (aCategory1Match !== bCategory1Match) {
        return bCategory1Match ? 1 : -1; // true sorts before false
      }

      // Priority 2: Category2 matches second
      const aCategory2Match = hasCategory2Match(a);
      const bCategory2Match = hasCategory2Match(b);
      if (aCategory2Match !== bCategory2Match) {
        return bCategory2Match ? 1 : -1;
      }

      // Priority 3: Category3 matches third
      const aCategory3Match = hasCategory3Match(a);
      const bCategory3Match = hasCategory3Match(b);
      if (aCategory3Match !== bCategory3Match) {
        return bCategory3Match ? 1 : -1;
      }

      // Priority 4: LastName alphabetical
      const lastNameCompare = (a.LastName || '').localeCompare(b.LastName || '');
      if (lastNameCompare !== 0) return lastNameCompare;

      // Priority 5: FirstName alphabetical
      return (a.FirstName || '').localeCompare(b.FirstName || '');
    });
  };

  // Handler for filter changes
  const handleFilterChange = (selectedOptions: Selectable[]) => {
    setFilters(selectedOptions || []);
  };

  const buildJudge = (judge: IJudgeInfoSummary, i: number) => {
    if (!showAssigned && judge.AssignedCategory) return null;

    let tags = judge.TagsCsv?.split(',')
      .map((x) => tagDic[+x])
      .filter((x) => x);
    if (i < 10) console.log(judge, tags);
    return (
      <div key={`judge-sum-${i}`} className="judge-info">
        <div className="flex-between">
          <div>
            <span className="name">
              {judge.LastName}, {judge.FirstName}
            </span>
            <div className="tag-bar">
              {tags &&
                tags.map((x, i) => {
                  return <TagComponent key={`judge-tag-${i}`} tag={x} />;
                })}
            </div>

            {(judge.CategoricalJudge ? false : true) && (
              <div>
                <span className="label label-warning">
                  <Icon type={IconType.warning} /> Non-Categorical Judge
                </span>
              </div>
            )}
          </div>
          {/* <td>{judge.Email}</td>
            <td>{judge.Phone}</td> */}
          <div></div>
          <div>
            <select
              className="form-control"
              value={judge.AssignedCategory?.toString() ?? '0'}
              onChange={(e) => {
                updateCategory(judge, +e.target.value);
              }}>
              <option value="0">select</option>
              {info?.Categories.map((x, i) => {
                return (
                  <option key={`${judge.PersonId}-cate-id-${i}`} value={x.id}>
                    {x.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="judge-prefs">
          {oldCategoryDic[judge.PersonId] && (
            <span title="Previouse category">
              <Icon type={IconType.history} /> {info?.CategoryDic[oldCategoryDic[judge.PersonId]]}{' '}
            </span>
          )}
          {info?.CategoryDic[judge.Category1] && <span>{info?.CategoryDic[judge.Category1]}</span>}
          {info?.CategoryDic[judge.Category2] && <span>{info?.CategoryDic[judge.Category2]}</span>}
          {info?.CategoryDic[judge.Category3] && <span>{info?.CategoryDic[judge.Category3]}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="judge-assign-cate-title-bar sticky">
        <div className="flex-between">
          <h3>Assign Judges to Their Category</h3>
          <div>
            {info && (
              <Select
                placeholder={'Category Preference Filter'}
                options={info.Categories.map((c, i) => ({ label: c.name, value: c.id }))}
                value={filters ?? []}
                onChange={handleFilterChange}
                isMulti
                isClearable={true}
                menuPlacement={'auto'}
              />
            )}
          </div>
          <div className="btn-group">
            

            { info?.TrackingJudgesPlanToParticipate &&
              <Button
                onClick={() => {
                  setShowParticipating(!showParticipating);
                }}
                text={showParticipating ? 'Hide Participating' : 'Show Participating'}
                type={showParticipating ? 'on' : 'off'}
              />
            }


            <Button
              onClick={() => {
                setShowStats(!showStats);
              }}
              text={showStats ? 'Hide Stats' : 'Show Stats'}
              type={showStats ? 'on' : 'off'}
            />
            <Button
              onClick={() => {
                setShowAssigned(!showAssigned);
              }}
              text={showAssigned ? 'Hide Assigned' : 'Show Assigned'}
              type={showAssigned ? 'on' : 'off'}
            />
            <Button
              onClick={() => {
                clearAllAssignments();
              }}
              text="Clear All Assignments"
              type="delete"
            />
          </div>
        </div>
        {showStats && (
          <div className="stats">
            {stats.map((x, i) => {
              return (
                <div key={`jactb-s-${i}`}>
                  <strong>{x.name}</strong>{' '}
                  <span>
                    Judges: {x.numberOfJudges} Projects: {x.numberOfProjects}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="assign-judge-wiz">
        {/* <div className="search-bar">
                search goes here
            </div> */}
        <div className="judge-list">{info && <>{getFilteredAndSortedJudges(info.Judges || [], filters).map(buildJudge)}</>}</div>
      </div>
    </>
  );
};

export default ReduxMap(AssignJudgesToCategoryWizard);
